import { fetchApi } from '@/lib/api';
import type {
  TaskWebhookStatus,
  TaskAttachment,
  TaskBoard,
  TaskCard,
  TaskColumn,
  TaskContextRef,
  TaskContextSearchHit,
  TaskCreateInput,
  TaskDetail,
  TaskFinishedPage,
  TaskLabel,
  TaskLabelColor,
  TaskMoveInput,
  TaskNotification,
  TaskSlackStatus,
  TaskType,
  TaskTypeField,
  TaskUpdateInput,
  TaskUpdateResponse,
} from '@shared/types/tasks';
import { TASK_BOARD_DONE_LIMIT } from '@shared/types/tasks';

const base = '/tasks';

/**
 * Tasks created optimistically (with an id chosen here) are usable before the server has them:
 * every request about that task waits for its create to finish first.
 */
const creating = new Map<string, Promise<unknown>>();

async function created(taskId: string): Promise<void> {
  await creating.get(taskId)?.catch(() => undefined);
}

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

export function scopeParam(scope: TaskContextRef[]): string {
  return scope.map((ref) => `${ref.type}:${encodeURIComponent(ref.id)}`).join(',');
}

/** `done`: how many finished tasks the Board loads (Show older raises it; T-377). A scoped board has them all. */
export function getTaskBoard(opts: { scope?: TaskContextRef[]; archived?: boolean } = {}, done?: number) {
  const params = new URLSearchParams();
  if (opts.scope?.length) params.set('scope', scopeParam(opts.scope));
  if (opts.archived) params.set('archived', '1');
  if (done && done !== TASK_BOARD_DONE_LIMIT && !opts.scope?.length) params.set('done', String(done));
  const qs = params.toString();
  return fetchApi<TaskBoard>(`${base}/board${qs ? `?${qs}` : ''}`);
}

/** Older finished tasks the Board's search finds (T-377). */
export function getFinishedTasks(opts: { archived?: boolean; q: string }) {
  const params = new URLSearchParams({ q: opts.q });
  if (opts.archived) params.set('archived', '1');
  return fetchApi<TaskFinishedPage>(`${base}/finished?${params.toString()}`);
}

/** Fresh copies of some cards (and the cards Waiting on them), for Boards already loaded (T-377). */
export function getTaskCards(taskIds: string[]) {
  return fetchApi<Pick<TaskBoard, 'tasks' | 'tags' | 'members'>>(`${base}/cards?ids=${taskIds.join(',')}`);
}

export async function getTask(taskId: string) {
  await created(taskId);
  return fetchApi<{ task: TaskDetail }>(`${base}/${taskId}`);
}

export function createTask(input: TaskCreateInput) {
  const request = (input.label_ids?.length ? labelsCreated() : Promise.resolve()).then(() => fetchApi<{ task: TaskCard }>(base, json('POST', input)));
  if (input.id) {
    const id = input.id;
    creating.set(id, request);
    void request.finally(() => creating.delete(id));
  }
  return request;
}

/** The last edit sent for each task. Edits go one at a time so a quick assign-then-unassign lands in order. */
const updating = new Map<string, Promise<unknown>>();

export function updateTask(taskId: string, input: TaskUpdateInput) {
  const previous = updating.get(taskId);
  const request = (async () => {
    await previous?.catch(() => undefined);
    await created(taskId);
    if (input.label_ids) await labelsCreated();
    return fetchApi<TaskUpdateResponse>(`${base}/${taskId}`, json('PATCH', input));
  })();
  updating.set(taskId, request);
  void request.finally(() => {
    if (updating.get(taskId) === request) updating.delete(taskId);
  });
  return request;
}

export async function moveTask(taskId: string, input: TaskMoveInput) {
  await created(taskId);
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/move`, json('POST', input));
}

export async function addTaskContexts(taskId: string, contexts: Array<TaskContextRef & { title?: string }>) {
  await created(taskId);
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/contexts`, json('POST', { contexts }));
}

export async function removeTaskContext(taskId: string, ref: TaskContextRef) {
  await created(taskId);
  const params = new URLSearchParams({ type: ref.type, id: ref.id });
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/contexts?${params}`, { method: 'DELETE' });
}

/** `id` (chosen here) lets an optimistic message match the saved one, and makes retries safe. */
export async function postTaskChat(taskId: string, body: string, attachmentIds: string[] = [], id?: string) {
  await created(taskId);
  return fetchApi<{ event_id: string }>(`${base}/${taskId}/chat`, json('POST', { id, body, attachment_ids: attachmentIds }));
}

export async function editTaskChat(taskId: string, eventId: string, body: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/chat/${eventId}`, json('PATCH', { body }));
}

export async function deleteTaskChat(taskId: string, eventId: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/chat/${eventId}`, { method: 'DELETE' });
}

export async function uploadTaskAttachment(taskId: string, file: File) {
  await created(taskId);
  const form = new FormData();
  form.append('file', file, file.name || 'pasted-image.png');
  return fetchApi<{ attachment: TaskAttachment }>(`${base}/${taskId}/attachments`, { method: 'POST', body: form });
}

export async function deleteTaskAttachment(taskId: string, attachmentId: string) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

/** Summarise again (T-318): a new AI summary now. Answers with the card, summary included. */
export async function summariseTask(taskId: string) {
  await created(taskId);
  return fetchApi<{ task: TaskCard }>(`${base}/${taskId}/summary`, { method: 'POST' });
}

export async function setTaskFollowing(taskId: string, following: boolean) {
  await created(taskId);
  return fetchApi<{ ok: true }>(`${base}/${taskId}/follow`, json('POST', { following }));
}

export function searchTaskContexts(q: string) {
  return fetchApi<{ results: TaskContextSearchHit[] }>(`${base}/context-search?q=${encodeURIComponent(q)}`);
}

export function getTaskNotifications() {
  return fetchApi<{ notifications: TaskNotification[]; unread: number }>(`${base}/notifications`);
}

export function markTaskNotificationsRead(opts: { ids?: string[]; task_id?: string } = {}) {
  return fetchApi<{ ok: true }>(`${base}/notifications/read`, json('POST', opts));
}

export function createTaskColumn(input: { name: string; is_done?: boolean }) {
  return fetchApi<{ column: TaskColumn }>(`${base}/columns`, json('POST', input));
}

export function updateTaskColumn(columnId: string, input: { name?: string; is_done?: boolean }) {
  return fetchApi<{ ok: true }>(`${base}/columns/${columnId}`, json('PATCH', input));
}

export function reorderTaskColumns(ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/columns/reorder`, json('POST', { ids }));
}

export function deleteTaskColumn(columnId: string) {
  return fetchApi<{ ok: true }>(`${base}/columns/${columnId}`, { method: 'DELETE' });
}

/** Labels being created; saving a task's labels waits for them so the new label isn't dropped. */
const creatingLabels = new Set<Promise<unknown>>();

async function labelsCreated(): Promise<void> {
  await Promise.all([...creatingLabels].map((p) => p.catch(() => undefined)));
}

export function createTaskLabel(input: { id?: string; name: string; color: TaskLabelColor }) {
  const request = fetchApi<{ label: TaskLabel }>(`${base}/labels`, json('POST', input));
  creatingLabels.add(request);
  void request.finally(() => creatingLabels.delete(request));
  return request;
}

export function updateTaskLabel(labelId: string, input: { name?: string; color?: TaskLabelColor }) {
  return fetchApi<{ ok: true }>(`${base}/labels/${labelId}`, json('PATCH', input));
}

export function deleteTaskLabel(labelId: string) {
  return fetchApi<{ ok: true }>(`${base}/labels/${labelId}`, { method: 'DELETE' });
}

/* Types (T-331): name, colour and which fields each shows. */

export function createTaskType(input: { name: string; color: TaskLabelColor; fields: TaskTypeField[] }) {
  return fetchApi<{ type: TaskType }>(`${base}/types`, json('POST', input));
}

export function updateTaskType(typeId: string, input: { name?: string; color?: TaskLabelColor; fields?: TaskTypeField[] }) {
  return fetchApi<{ ok: true }>(`${base}/types/${typeId}`, json('PATCH', input));
}

export function reorderTaskTypes(ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/types/reorder`, json('POST', { ids }));
}

export function deleteTaskType(typeId: string) {
  return fetchApi<{ ok: true }>(`${base}/types/${typeId}`, { method: 'DELETE' });
}

/** The Attachments' new order: the top image or video is the task's cover (T-331). */
export function reorderTaskAttachments(taskId: string, ids: string[]) {
  return fetchApi<{ ok: true }>(`${base}/${taskId}/attachments/order`, json('POST', { ids }));
}

/** The task webhook's last success and failure (T-40); `webhook` is null when the team has none. */
export function getTaskWebhookStatus(options?: { signal?: AbortSignal }) {
  return fetchApi<{ webhook: TaskWebhookStatus | null }>(`${base}/webhook`, options);
}

export function getTaskSlackStatus() {
  return fetchApi<TaskSlackStatus>(`${base}/slack`);
}

export function getTaskSlackInstallUrl() {
  return fetchApi<{ url: string }>(`${base}/slack/install`, { method: 'POST' });
}

export function disconnectTaskSlack() {
  return fetchApi<{ ok: true }>(`${base}/slack`, { method: 'DELETE' });
}

export function setTaskSlackDms(dmEnabled: boolean) {
  return fetchApi<TaskSlackStatus>(`${base}/slack/me`, json('PATCH', { dm_enabled: dmEnabled }));
}
